import React from "react";
import SingleTimelineConnected from "../../components/Dashboard/HubletView/SingleTimeline/SingleTimeline";
import PnlSummaryChart from "../../components/Dashboard/PnlSummaryChart/PnlSummaryChart";
import RangeSelector from "../../components/Dashboard/range-selectors/RangeSelector";
import styled, { css } from "styled-components/macro";
import { Fade, useMediaQuery } from "@mui/material";
import {
  BREAKPOINT_DESKTOP,
  BREAKPOINT_MOBILE,
  CARD_INNER_PADDING,
} from "constants/constants";
import { HighlightTagsSelector } from "components/Dashboard/HubletView/SingleTimeline/HighlightTagsSelector/HighlightTagsSelector";
import CardWithControls from "components/Dashboard/CardWithControls/CardWithControls";
import {
  DASHBOARD_CARD_PROPS,
  PNL_SUMMARY_CHART_CARD_CSS,
} from "pages/DashboardPages/DashboardPagesContent/dashboardPagesContentUtils";
import {
  useContainerDimensionsObserver,
  useGetPageFromPath,
} from "utils/customHooks/genericHooks";
import { BtnExpandCollapseTimeline } from "components/Dashboard/HubletView/SingleTimeline/HighlightTagsSelector/BtnExpandCollapseTimeline";
import ServiceOutageIndicator from "components/SimpleComponents/TopBanner/ServiceOutageIndicator";
import TimelineCardControls from "components/Dashboard/Timeline/TimelineCardControls/TimelineCardControls";
import RangeSelectorConfig from "components/Dashboard/range-selectors/RangeSelectorConfig";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useIsMiniMode } from "components/Dashboard/HubletView/hubletViewUtils";
import PnlChartCardControls from "components/Dashboard/PnlSummaryChart/PnlChartCardControls";
import { TodaysProductionCardHubletPage } from "./TodaysProductionCardHubletPage";

const DatepickerAndCharts = React.memo(() => {
  return (
    <DatepickerAndChartsStyles className="datepickerAndCharts">
      <div
        className={`datepickerAndCharts-size-observer cardsWrapper newDatePicker`}
      >
        <ServiceOutageIndicator
          customCss={css`
            margin: 5px 5px 0;
            @media (min-width: ${BREAKPOINT_MOBILE}px) {
              margin: 10px 10px 0;
            }
            position: fixed;
            z-index: 1000;
            bottom: 8px;
            opacity: 0.9;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
            @media (max-width: ${BREAKPOINT_DESKTOP - 1}px) {
              left: 0;
              right: 0;
            }
          `}
        />
        <RangeSelectorCardHubletPage />
        <TodaysProductionCardHubletPage />
        <SingleTimelineCardHubletPage />
        <PnlSummaryChartCardHubletPageFadeInOut />
        {/* spacer just to clarify that this is the end of the page */}
        <div className="spacer" style={{ height: 40 }} />
      </div>
    </DatepickerAndChartsStyles>
  );
});

function PnlSummaryChartCardHubletPageFadeInOut() {
  const [isMiniMode] = useIsMiniMode();
  const isDesktopOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINT_DESKTOP}px)`
  );
  const isPrint = useMediaQuery("print");
  const showPnlChartAtBottom = isPrint || !isMiniMode || !isDesktopOrLarger;
  return (
    <Fade in={showPnlChartAtBottom} timeout={500} unmountOnExit={true}>
      <div className="PnlSummaryChartCardHubletPageFadeInOut">
        <PnlSummaryChartCardHubletPage />
      </div>
    </Fade>
  );
}

export default DatepickerAndCharts;

export const DatepickerAndChartsStyles = styled.div`
  .overallBarstackWrapper {
    padding: 0 ${CARD_INNER_PADDING}px;
  }
  .cardsWrapper {
    max-width: 100%;
    &.overflowVisible {
      overflow: visible;
    }
    @media (min-width: ${BREAKPOINT_DESKTOP}px) {
      @supports (overflow: overlay) {
        max-width: calc(100% + 8px);
        margin-right: -8px;
      }
    }
  }
`;

export function RangeSelectorCardHubletPage({
  autoSubmit,
}: {
  autoSubmit?: boolean;
}) {
  return (
    <CardWithControls
      title={""}
      {...DASHBOARD_CARD_PROPS}
      className={"Card_RangeSelector"}
      collapsible={false}
      customCss={css`
        overflow: visible;
        &&& {
          margin-top: 8px;
        }
        .cardConfigControls {
          margin-right: 2px;
        }
        .cardContent {
          margin-top: -8px;
          padding-bottom: 16px;
        }
        /* horizontal layout * using media query as cheaper hack than more accurate useContainerDimensions().width > BREAKPOINT_DESKTOP - switch if needed */
        @media (min-width: ${2444}px) {
          [class*="DateAndShiftPickerStyles"] {
            padding: 0 16px 16px;
            margin-top: -8px;
          }
        }
      `}
      CardConfigControls={<RangeSelectorConfig />}
    >
      <ErrorBoundary small={true} id="DatepickerAndCharts_RangeSelector">
        <RangeSelector {...{ autoSubmit }} />
      </ErrorBoundary>
    </CardWithControls>
  );
}
export function PnlSummaryChartCardHubletPage() {
  return (
    <CardWithControls
      className={"Card_PnlChart Card_PnlChart_HubletPage"}
      id={"Card_PnlChart_HubletPage"}
      title="Performance_&_Loss_Breakdown"
      {...DASHBOARD_CARD_PROPS}
      customCss={PNL_SUMMARY_CHART_CARD_CSS}
      CardConfigControls={<PnlChartCardControls />}
    >
      <ErrorBoundary small={true} id="DatepickerAndCharts_PnlSummaryChart">
        <PnlSummaryChart />
      </ErrorBoundary>
    </CardWithControls>
  );
}

export function SingleTimelineCardHubletPage() {
  const { width } = useContainerDimensionsObserver(
    ".datepickerAndCharts-size-observer"
  );
  const { isHubletPage } = useGetPageFromPath();
  const [
    {
      general: { showSkuTimeline, showOperatorTimeline },
    },
  ] = useFlightConfig();
  const showBtnExpandCollapseTimeline =
    isHubletPage && (showSkuTimeline || showOperatorTimeline);
  return (
    <CardWithControls
      className={"Card_Timeline break-after"}
      id={"Card_Timeline"}
      title="Timeline"
      {...DASHBOARD_CARD_PROPS}
      // need overflow visible for the timeline tooltip to show up
      customCss={css`
        overflow: visible;
        .btnExpandCollapseTimeline {
          position: absolute;
          right: 24px;
          bottom: 24px;
        }
      `}
      CardConfigControls={<TimelineCardControls />}
    >
      <ErrorBoundary
        small={true}
        id="DatepickerAndCharts_SingleTimelineConnected"
      >
        <SingleTimelineConnected containerDimensionsWidth={width} />
      </ErrorBoundary>
      <ErrorBoundary
        small={true}
        id="DatepickerAndCharts_HighlightTagsSelector"
      >
        <HighlightTagsSelector />
      </ErrorBoundary>
      {showBtnExpandCollapseTimeline && (
        <div className="btnExpandCollapseTimeline">
          <BtnExpandCollapseTimeline />
        </div>
      )}
    </CardWithControls>
  );
}
